"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.readArt = exports.readArtUpdates = exports.isArtCurrent = exports.readArtSummary = exports.EMPTY_ART = exports.PART_TYPES = exports.MULTICALL3 = void 0;
const abi_1 = require("@ethersproject/abi");
const zlib_1 = require("zlib");
// Multicall3 has the same address on mainnet and most other chains
exports.MULTICALL3 = '0xcA11bde05977b3631167028862bE2a173976CA11';
const multicallAbi = new abi_1.Interface([
    'function aggregate3((address target, bool allowFailure, bytes callData)[] calls) payable returns ((bool success, bytes returnData)[] returnData)',
]);
const descriptorAbi = new abi_1.Interface([
    'function art() view returns (address)',
    'function attribute() view returns (address)',
    'function backgroundCount() view returns (uint256)',
    'function bodyCount() view returns (uint256)',
    'function accessoryCount() view returns (uint256)',
    'function headCount() view returns (uint256)',
    'function glassesCount() view returns (uint256)',
    'function backgrounds(uint256 index) view returns (string)',
    'function palettes(uint8 index) view returns (bytes)',
]);
const TRAIT = 'tuple(tuple(uint16 imageCount, uint80 decompressedLength, address pointer)[] storagePages, uint256 storedImagesCount)';
const artAbi = new abi_1.Interface([
    `function getBodiesTrait() view returns (${TRAIT})`,
    `function getAccessoriesTrait() view returns (${TRAIT})`,
    `function getHeadsTrait() view returns (${TRAIT})`,
    `function getGlassesTrait() view returns (${TRAIT})`,
]);
const attributeAbi = new abi_1.Interface([
    'function backgrounds(uint256 index) view returns (string)',
    'function bodies(uint256 index) view returns (string)',
    'function accessories(uint256 index) view returns (string)',
    'function heads(uint256 index) view returns (string)',
    'function glasses(uint256 index) view returns (string)',
]);
const PARTS = {
    bodies: { count: 'bodyCount', trait: 'getBodiesTrait', prefix: 'body' },
    accessories: { count: 'accessoryCount', trait: 'getAccessoriesTrait', prefix: 'accessory' },
    heads: { count: 'headCount', trait: 'getHeadsTrait', prefix: 'head' },
    glasses: { count: 'glassesCount', trait: 'getGlassesTrait', prefix: 'glasses' },
};
exports.PART_TYPES = Object.keys(PARTS);
exports.EMPTY_ART = {
    bgcolors: [],
    bgnames: [],
    palette: [],
    images: { bodies: [], accessories: [], heads: [], glasses: [] },
};
const range = (from, to) => Array.from({ length: Math.max(to - from, 0) }, (_, i) => from + i);
/** Many view calls in one eth_call; a call that fails (e.g. a name not added yet) comes back undefined */
const aggregate = (provider, calls) => __awaiter(void 0, void 0, void 0, function* () {
    if (!calls.length)
        return [];
    const data = multicallAbi.encodeFunctionData('aggregate3', [
        calls.map(({ target, abi, fn, args = [] }) => ({
            target,
            allowFailure: true,
            callData: abi.encodeFunctionData(fn, args),
        })),
    ]);
    const [results] = multicallAbi.decodeFunctionResult('aggregate3', yield provider.call({ to: exports.MULTICALL3, data }));
    return results.map(({ success, returnData }, i) => {
        if (!success)
            return undefined;
        try {
            return calls[i].abi.decodeFunctionResult(calls[i].fn, returnData);
        }
        catch (_a) {
            return undefined;
        }
    });
});
/** `aggregate` in chunks, so no one eth_call runs into an RPC's gas cap */
const aggregateInChunks = (provider, calls, size = 150) => __awaiter(void 0, void 0, void 0, function* () {
    const chunks = range(0, Math.ceil(calls.length / size)).map(i => calls.slice(i * size, (i + 1) * size));
    return (yield Promise.all(chunks.map(chunk => aggregate(provider, chunk)))).flat();
});
/** Results of calls that all had to succeed */
const allRead = (results, what) => {
    if (results.some(result => !result))
        throw new Error(`Could not read ${what}`);
    return results;
};
/** Palette bytes, 3 per colour, as the hex colours the SVG builder takes; colour 0 is transparent */
const toPalette = (bytes) => { var _a; return ((_a = bytes.replace(/^0x/, '').match(/.{6}/g)) !== null && _a !== void 0 ? _a : []).map((colour, i) => (i === 0 ? '' : colour)); };
const samePalette = (a, b) => a.length === b.length && a.every((colour, i) => colour === b[i]);
/** An image's filename in the style of the bundled ones, e.g. "head-console-handheld" */
const toFilename = (type, index, name) => `${PARTS[type].prefix}-${name ? name.trim().toLowerCase().split(/\s+/).join('-') : index}`;
/**
 * The descriptor's art and attribute contracts, trait counts and palette, in one eth_call.
 * @param provider A JSON-RPC connection
 * @param descriptor The AlpsDescriptorV2 address
 */
const readArtSummary = (provider, descriptor) => __awaiter(void 0, void 0, void 0, function* () {
    const fns = ['art', 'attribute', 'backgroundCount', ...Object.values(PARTS).map(p => p.count)];
    const results = yield aggregate(provider, [
        ...fns.map(fn => ({ target: descriptor, abi: descriptorAbi, fn })),
        { target: descriptor, abi: descriptorAbi, fn: 'palettes', args: [0] },
    ]);
    const [art, attribute, backgrounds, bodies, accessories, heads, glasses, palette] = allRead(results, `the descriptor at ${descriptor}`).map(result => result[0]);
    return {
        art,
        attribute,
        counts: {
            backgrounds: backgrounds.toNumber(),
            bodies: bodies.toNumber(),
            accessories: accessories.toNumber(),
            heads: heads.toNumber(),
            glasses: glasses.toNumber(),
        },
        palette: toPalette(palette),
    };
});
exports.readArtSummary = readArtSummary;
const missingNames = (names, count) => range(0, count).filter(i => !names[i]);
/** Whether `art` has every trait, name and colour the chain has (it may have more, e.g. on a testnet) */
const isArtCurrent = ({ counts, palette }, art) => samePalette(palette, art.palette) &&
    art.bgcolors.length >= counts.backgrounds &&
    !missingNames(art.bgnames, counts.backgrounds).length &&
    exports.PART_TYPES.every(type => art.images[type].length >= counts[type] &&
        !missingNames(art.images[type].map(part => part.name), counts[type]).length);
exports.isArtCurrent = isArtCurrent;
/**
 * One page of a trait's images. AlpsArt stores each page with SSTORE2, as the code of a contract of its
 * own: a STOP byte, then the page's images abi-encoded as bytes[] and deflated. Reading that code and
 * inflating it here takes one cheap eth_getCode, where the contract's heads(i) inflates the whole page on
 * chain for every image.
 */
const readPage = (provider, page) => __awaiter(void 0, void 0, void 0, function* () {
    const code = yield provider.getCode(page.pointer);
    if (!code.startsWith('0x00'))
        throw new Error(`No art stored at ${page.pointer}`);
    const inflated = (0, zlib_1.inflateRawSync)(Buffer.from(code.slice(4), 'hex'));
    if (inflated.length !== page.decompressedLength) {
        throw new Error(`The page at ${page.pointer} inflated to the wrong length`);
    }
    const [images] = abi_1.defaultAbiCoder.decode(['bytes[]'], inflated);
    if (images.length !== page.imageCount) {
        throw new Error(`The page at ${page.pointer} holds the wrong number of images`);
    }
    return images;
});
/** The images from index `from` up to `to`, reading only the pages they're on */
const readImages = (provider, pages, from, to) => __awaiter(void 0, void 0, void 0, function* () {
    let pageStart = 0;
    const reads = pages.map(page => {
        const start = pageStart;
        const end = (pageStart += page.imageCount);
        if (end <= from || start >= to)
            return Promise.resolve([]);
        return readPage(provider, page).then(images => images.slice(Math.max(from - start, 0), Math.min(to, end) - start));
    });
    const images = (yield Promise.all(reads)).flat();
    if (images.length !== to - from)
        throw new Error('The art has fewer images than its count');
    return images;
});
/**
 * `art` brought up to date with the chain: the parts and backgrounds past its counts, names it lacks, and
 * the palette if it changed. Only what's missing is read, and `art` itself comes back if it was current.
 * @param provider A JSON-RPC connection
 * @param descriptor The AlpsDescriptorV2 address
 * @param summary The descriptor's summary, from `readArtSummary`
 * @param art The art to bring up to date; everything is read when it's left out
 */
const readArtUpdates = (provider, descriptor, summary, art = exports.EMPTY_ART) => __awaiter(void 0, void 0, void 0, function* () {
    if ((0, exports.isArtCurrent)(summary, art))
        return art;
    const { counts } = summary;
    const grown = exports.PART_TYPES.filter(type => counts[type] > art.images[type].length);
    const bgNameIndexes = missingNames(art.bgnames, counts.backgrounds);
    const partNameIndexes = exports.PART_TYPES.map(type => missingNames(art.images[type].map(part => part.name), counts[type]));
    const [traitResults, bgcolorResults, names] = yield Promise.all([
        aggregate(provider, grown.map(type => ({ target: summary.art, abi: artAbi, fn: PARTS[type].trait }))),
        aggregateInChunks(provider, range(art.bgcolors.length, counts.backgrounds).map(i => ({
            target: descriptor,
            abi: descriptorAbi,
            fn: 'backgrounds',
            args: [i],
        }))),
        aggregateInChunks(provider, [
            ...bgNameIndexes.map(i => ({
                target: summary.attribute,
                abi: attributeAbi,
                fn: 'backgrounds',
                args: [i],
            })),
            ...exports.PART_TYPES.flatMap((type, t) => partNameIndexes[t].map(i => ({
                target: summary.attribute,
                abi: attributeAbi,
                fn: type,
                args: [i],
            }))),
        ]),
    ]);
    const traits = allRead(traitResults, 'the art pages');
    const bgcolors = allRead(bgcolorResults, 'the backgrounds').map(result => result[0]);
    // The names found, keyed like "heads/12"
    const found = new Map();
    const nameKeys = [
        ...bgNameIndexes.map(i => `backgrounds/${i}`),
        ...exports.PART_TYPES.flatMap((type, t) => partNameIndexes[t].map(i => `${type}/${i}`)),
    ];
    names.forEach((result, i) => (result === null || result === void 0 ? void 0 : result[0]) && found.set(nameKeys[i], result[0]));
    const images = yield Promise.all(grown.map((type, i) => {
        const pages = traits[i][0].storagePages.map((page) => ({
            imageCount: Number(page.imageCount),
            decompressedLength: Number(page.decompressedLength),
            pointer: page.pointer,
        }));
        return readImages(provider, pages, art.images[type].length, counts[type]);
    }));
    const paletteChanged = !samePalette(summary.palette, art.palette);
    if (!grown.length && !bgcolors.length && !found.size && !paletteChanged)
        return art;
    const updatedImages = Object.assign({}, art.images);
    exports.PART_TYPES.forEach(type => {
        var _a;
        const have = art.images[type];
        const named = have.map((part, i) => {
            const name = found.get(`${type}/${i}`);
            return name ? { filename: part.filename, name, data: part.data } : part;
        });
        const added = ((_a = images[grown.indexOf(type)]) !== null && _a !== void 0 ? _a : []).map((data, j) => {
            const name = found.get(`${type}/${have.length + j}`);
            return Object.assign(Object.assign({ filename: toFilename(type, have.length + j, name) }, (name ? { name } : {})), { data });
        });
        updatedImages[type] = [...named, ...added];
    });
    const allBgcolors = [...art.bgcolors, ...bgcolors];
    return {
        bgcolors: allBgcolors,
        bgnames: allBgcolors.map((_, i) => art.bgnames[i] || found.get(`backgrounds/${i}`) || ''),
        palette: paletteChanged ? summary.palette : art.palette,
        images: updatedImages,
    };
});
exports.readArtUpdates = readArtUpdates;
/**
 * All the art on chain: palette, backgrounds, every part's image and every trait's name, in the format of
 * the bundled `ImageData`.
 * @param provider A JSON-RPC connection
 * @param descriptor The AlpsDescriptorV2 address
 */
const readArt = (provider, descriptor) => __awaiter(void 0, void 0, void 0, function* () { return (0, exports.readArtUpdates)(provider, descriptor, yield (0, exports.readArtSummary)(provider, descriptor)); });
exports.readArt = readArt;
